import {apiClient} from 'common/util/api-client';
import gdpr from 'common/report/util/gdpr-globals';

import {activateTracking, deactivateTracking} from './tracking';
import {activateStorage, deactivateStorage} from './storage';
import {activateSnippets, removeSnippets} from './snippets';

export const TRACKING_PREFIX = `__mp_opt_in_out_tracking_`;
export const COOKIES_PREFIX = `__mp_opt_in_out_cookies_`;

/**
 * Check or set the main Mixpanel SDK's opt-in/out status (in browser cookie and/or localStorage)
 * @param {string} opt_fn - function to be called with the following params
 *                          (one of has_opted_<in/out>_tracking, opt_<in/out>_tracking, clear_opt_in_out_tracking)
 * @param {string} trackingOrStorage - opt in/out of tracking or browser storage (must be "tracking" or "storage")
 * @param {string} persistenceType - storage mechanism used to store the opt status in the browser (must be "localStorage" or "cookie")
 * @returns {boolean|undefined} boolean if `getOrSet` is "get", otherwise undefined
 */
function _mixpanelSdkOpt(optFn, trackingOrStorage, persistenceType) {
  if (
    ![
      `has_opted_in_tracking`,
      `has_opted_out_tracking`,
      `opt_in_tracking`,
      `opt_out_tracking`,
      `clear_opt_in_out_tracking`,
    ].includes(optFn) ||
    ![`tracking`, `storage`].includes(trackingOrStorage) ||
    ![`cookie`, `localStorage`].includes(persistenceType)
  ) {
    throw new Error(`invalid input`);
  }

  const isTracking = trackingOrStorage === `tracking`;
  if (window.mixpanel) { // To pass fss navigation test
    return window.mixpanel[optFn]({
      'cookie_prefix': isTracking ? TRACKING_PREFIX : COOKIES_PREFIX,
      'track_properties': {type: isTracking ? `tracking` : `cookies`},
      'persistence_type': persistenceType,
      'cross_subdomain_cookie': true,
    });
  }
}

// Convenience wrapper for _mixpanelSdkOpt
const mixpanelSdkOpt = {
  in: {
    get: (...args) => _mixpanelSdkOpt(`has_opted_in_tracking`, ...args),
    set: (...args) => _mixpanelSdkOpt(`opt_in_tracking`, ...args),
  },
  out: {
    get: (...args) => _mixpanelSdkOpt(`has_opted_out_tracking`, ...args),
    set: (...args) => _mixpanelSdkOpt(`opt_out_tracking`, ...args),
  },
  undecided: {
    get: (...args) => !mixpanelSdkOpt.in.get(...args) && !mixpanelSdkOpt.out.get(...args),
    set: (...args) => _mixpanelSdkOpt(`clear_opt_in_out_tracking`, ...args),
  },
};

/**
 * Change the current opt status for tracking or cookies to in, out, or undecided (based on passed params)
 * This sets the opt state in 3 places:
 *   - Mixpanel lib cookies
 *   - mp.globals.gdpr object
 *   - user profile on backend/appdb
 *
 * @param {string} optState - opt-in or opt-out (must be "in", "out", or "undecided")
 * @param {string} trackingOrStorage - opt in/out of tracking or browser storage (must be "tracking" or "storage")
 * @returns {Object} an object containing `success` and `error` keys indicating the result of the server request
 */
export async function setOptState(optState, trackingOrStorage) {
  if (![`in`, `out`, `undecided`].includes(optState) || ![`tracking`, `storage`].includes(trackingOrStorage)) {
    throw new Error(`invalid input`);
  }

  let success = true;
  let error = null;

  const isTracking = trackingOrStorage === `tracking`;
  const optType = isTracking ? `tracking` : `cookies`;

  if ((isTracking ? gdpr.optTracking : gdpr.optCookies) !== optState) {
    // update mp globals
    gdpr.update({[`opt_${optType}`]: optState});

    if (gdpr.isUserLoggedIn && !gdpr._settingUp2fa) {
      // update appdb
      try {
        await apiClient.post(`/api/app/gdpr/opt_${optState}/${optType}`);
        success = true;
      } catch (err) {
        error = err.message;
      }
    }
  }

  // activate tracking before opting in via Mixpanel libs below so we can track opt-in as "proof of consent"
  if (gdpr.allowTracking) {
    activateTracking();
  } else {
    deactivateTracking();
  }

  if (gdpr.allowStorage) {
    activateStorage();
  } else {
    deactivateStorage();
  }

  if (gdpr.allowSnippets) {
    activateSnippets();
  } else {
    removeSnippets();
  }

  // update clientside cookie & localStorage
  [`localStorage`, `cookie`].forEach((persistenceType) => {
    if (!mixpanelSdkOpt[optState].get(trackingOrStorage, persistenceType)) {
      mixpanelSdkOpt[optState].set(trackingOrStorage, persistenceType);
    }
  });

  return {success, error};
}

/**
 * Look at the opt state on client and server, and synchronize the states
 * based on the following set of rules for the 4 different cases:
 *   - server undecided, client undecided - reset opt statuses
 *   - server undecided, client decided   - take status from client
 *   - server decided,   client undecided - take status from server
 *   - server decided,   client decided   - take status from server
 */
export function synchronizeOptState() {
  [`tracking`, `storage`].forEach((trackingOrStorage) => {
    const isTracking = trackingOrStorage === `tracking`;
    const serverStatus = isTracking ? gdpr.optTracking : gdpr.optCookies;

    const serverOptedIn = serverStatus === `in`;
    const serverOptedOut = serverStatus === `out`;
    const serverUndecided = !serverOptedIn && !serverOptedOut;

    // refer to cookies as primary opt-out store since Mixpanel subdomains (e.g. helpcenter) cannot share localStorage
    let clientOptedIn = mixpanelSdkOpt.in.get(trackingOrStorage, `cookie`);
    let clientOptedOut = mixpanelSdkOpt.out.get(trackingOrStorage, `cookie`);
    let clientUndecided = !clientOptedIn && !clientOptedOut;

    // if no cookie opt flag present, check localStorage
    if (clientUndecided) {
      clientOptedIn = mixpanelSdkOpt.in.get(trackingOrStorage, `localStorage`);
      clientOptedOut = mixpanelSdkOpt.out.get(trackingOrStorage, `localStorage`);
      clientUndecided = !clientOptedIn && !clientOptedOut;
    }

    if (!serverUndecided) {
      setOptState(serverOptedIn ? `in` : `out`, trackingOrStorage);
    } else if (!clientUndecided) {
      setOptState(clientOptedIn ? `in` : `out`, trackingOrStorage);
    } else {
      setOptState(`undecided`, trackingOrStorage);
    }
  });
}
